<script setup lang="ts">
import type { FieldDef, NavigationFieldValue } from "./fieldRegistry";

const props = defineProps<{
  def: FieldDef;
  modelValue: NavigationFieldValue;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: NavigationFieldValue];
  unset: [];
}>();

const selectValue = computed(() => {
  if (props.def.kind !== "external" && props.def.kind !== "interactive") {
    return undefined;
  }

  return props.modelValue === undefined ? "inherit" : String(props.modelValue);
});

function emitText(value: unknown) {
  emit("update:modelValue", String(value ?? ""));
}

function emitBoolean(value: unknown) {
  if (value === "inherit") {
    emit("update:modelValue", undefined);
    return;
  }

  emit("update:modelValue", value === "true");
}
</script>

<template>
  <div class="flex items-start gap-2">
    <div class="w-[104px] shrink-0 pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {{ def.label }}
    </div>

    <div class="min-w-0 flex-1">
      <Textarea
        v-if="def.kind === 'textarea'"
        :model-value="String(modelValue ?? '')"
        class="min-h-[74px] rounded-sm bg-background text-[12.5px]"
        :placeholder="def.label"
        @update:model-value="emitText"
      />

      <EditorPathInput
        v-else-if="def.kind === 'image'"
        :model-value="String(modelValue ?? '')"
        mode="existing-file"
        :scopes="['site', 'external']"
        allow-http-urls
        content-type-filter="image"
        :placeholder="def.label"
        :aria-label="def.label"
        @update:model-value="emitText"
      />

      <EditorPathInput
        v-else-if="def.kind === 'path'"
        :model-value="String(modelValue ?? '')"
        mode="existing-file"
        :scopes="['site', 'external']"
        allow-http-urls
        :placeholder="def.label"
        :aria-label="def.label"
        @update:model-value="emitText"
      />

      <EditorThemeColourInput
        v-else-if="def.kind === 'colour'"
        :model-value="String(modelValue ?? '')"
        output="css"
        :placeholder="def.label"
        @update:model-value="emitText"
      />

      <Select
        v-else-if="def.kind === 'external' || def.kind === 'interactive'"
        :model-value="selectValue"
        @update:model-value="emitBoolean"
      >
        <SelectTrigger class="h-8 rounded-sm bg-background text-[12.5px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="inherit">Inherit</SelectItem>
          <SelectItem v-if="def.kind === 'external'" value="false">Same tab</SelectItem>
          <SelectItem v-if="def.kind === 'external'" value="true">New tab</SelectItem>
          <SelectItem v-if="def.kind === 'interactive'" value="true">Clickable</SelectItem>
          <SelectItem v-if="def.kind === 'interactive'" value="false">Not clickable</SelectItem>
        </SelectContent>
      </Select>

      <Input
        v-else
        :model-value="String(modelValue ?? '')"
        :class="[
          'h-8 rounded-sm bg-background text-[12.5px]',
          def.kind === 'mono' ? 'font-mono text-[11.5px]' : '',
        ]"
        :placeholder="def.label"
        @update:model-value="emitText"
      />
    </div>

    <Button
      type="button"
      variant="ghost"
      size="icon"
      class="mt-0.5 size-[26px] shrink-0 rounded-sm text-muted-foreground hover:text-destructive"
      title="Unset — inherit again"
      aria-label="Unset field"
      @click="emit('unset')"
    >
      <Icon mode="svg" size="13" name="lucide:x" />
    </Button>
  </div>
</template>
